// Local-first persistence for cart: prefers Telegram CloudStorage,
// falls back to localStorage when running outside Telegram.
import { z } from "zod";
import { CartItemSchema, type CartItem } from "@/shared/lib/db-schema";

const KEY = "eurostyle_cart_v1";
const CartItemListSchema = z.array(CartItemSchema);

function lsGet(): unknown {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function lsSet(value: ReadonlyArray<CartItem>): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* quota or disabled */
  }
}

// CloudStorage exists on window.Telegram.WebApp even in a plain browser
// (telegram-web-app.js is loaded), but its callbacks never fire outside a
// real Telegram client → the promise would hang and block hydration. Guard
// on initData (real session) and add a timeout backstop so we always fall
// back to localStorage in the browser.
function inTelegram(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}
function cloudGet(): Promise<string | null> {
  return new Promise((resolve) => {
    const cs = window.Telegram?.WebApp?.CloudStorage;
    if (!cs || !inTelegram()) return resolve(null);
    let done = false;
    const finish = (v: string | null): void => { if (!done) { done = true; resolve(v); } };
    setTimeout(() => finish(null), 800);
    try {
      cs.getItem(KEY, (err, value) => finish(err ? null : value ?? null));
    } catch {
      finish(null);
    }
  });
}
function cloudSet(value: string): Promise<boolean> {
  return new Promise((resolve) => {
    const cs = window.Telegram?.WebApp?.CloudStorage;
    if (!cs || !inTelegram()) return resolve(false);
    let done = false;
    const finish = (v: boolean): void => { if (!done) { done = true; resolve(v); } };
    setTimeout(() => finish(false), 800);
    try {
      cs.setItem(KEY, value, (err) => finish(!err));
    } catch {
      finish(false);
    }
  });
}

function parseList(raw: unknown): CartItem[] {
  if (!Array.isArray(raw)) return [];
  const r = CartItemListSchema.safeParse(raw);
  return r.success ? r.data : [];
}

export async function loadCart(): Promise<CartItem[]> {
  const fromCloud = await cloudGet();
  if (fromCloud) {
    try {
      return parseList(JSON.parse(fromCloud));
    } catch {
      return [];
    }
  }
  return parseList(lsGet());
}

export async function saveCart(items: ReadonlyArray<CartItem>): Promise<void> {
  lsSet(items);
  await cloudSet(JSON.stringify(items));
}
